import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonButton,
  IonAlert,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonIcon,
  IonText
} from '@ionic/react';
import { logOutOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { sendEmailVerification, signOut, User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const EmailVerification: React.FC = () => {
  const { t } = useTranslation();
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [lastVerificationSent, setLastVerificationSent] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const history = useHistory();
  const verificationCooldownSeconds = 120;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        if (user.emailVerified) {
          history.push('/teacher-selection');
        }
      } else {
        history.push('/login');
      }
    });

    return () => unsubscribe();
  }, [history]);

  useEffect(() => {
    if (lastVerificationSent) {
      const interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - lastVerificationSent;
        setTimeRemaining(Math.max(0, verificationCooldownSeconds - Math.floor(elapsed / 1000)));
        if (elapsed >= verificationCooldownSeconds * 1000) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [lastVerificationSent]);

  const resendVerificationEmail = async () => {
    try {
      const user = auth.currentUser as User;
      await sendEmailVerification(user);
      setLastVerificationSent(Date.now());
      setAlertMessage(t('emailVerification.resendSuccess'));
      setShowAlert(true);
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      setAlertMessage(t('emailVerification.resendError', { message: error.message }));
      setShowAlert(true);
    }
  };

  const refresh = () => {
    location.reload();
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      history.push('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const canResendVerification = !lastVerificationSent || Date.now() - lastVerificationSent > verificationCooldownSeconds * 1000;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle class='text-xl'>{t('emailVerification.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleLogout} color="danger">
              <IonIcon slot="icon-only" icon={logOutOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="h-full flex flex-col">
          <div className="flex-grow flex flex-col items-center justify-center">
            <IonText className="text-xl text-center">
              <h2 className="mb-4">{t('emailVerification.header')}</h2>
              <p className="mb-2">{t('emailVerification.message')}</p>
            </IonText>
          </div>
          
          <div className="w-full space-y-4 mb-8">
            {canResendVerification ? (
              <IonButton expand="block" onClick={resendVerificationEmail}>
                {t('emailVerification.resendButton')}
              </IonButton>
            ) : (
              <IonText color="medium" className="block text-center">
                <p>{t('emailVerification.resendCooldown', { seconds: timeRemaining })}</p>
              </IonText>
            )}
            <IonButton expand="block" onClick={refresh}>
              {t('common.ok')}
            </IonButton>
          </div>
        </div>

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('common.alert')}
          message={alertMessage}
          buttons={[t('common.ok')]}
        />
      </IonContent>
    </IonPage>
  );
};

export default EmailVerification;
